// frontend/src/components/ChatWindow.jsx
import React, { useEffect, useRef, useState } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import EmojiPicker from 'emoji-picker-react';
import {
  onNewConversationMessage,
  joinConversation,
  leaveConversation,
  startTyping,
  stopTyping,
  clearTypingConversation,
  requestTypingSnapshot,
  onTyping,
  onStopTyping,
  queryUsersPresence,
  onUserPresence,
} from '../services/socket';
import { getConversationMessages, sendMessage, getConversationInfo } from '../services/chatApi';
import { authenticatedRequest } from '../services/authApi';
import { REST_API_ORIGIN } from '../platform/endpoints';


import { formatRelativeTime } from '../utils/formatRelativeTime';
import { localizedValue } from '../localization/presentationLanguage';
import { getAuthToken } from '../utils/auth';

const chatText = (es, en) => localizedValue({ es, en });

// --- helper para avisarle al backend "yo ya leí hasta acá" ---
async function markConversationRead(token, conversationId) {
  if (!token || !conversationId) return;
  try {
    const base = REST_API_ORIGIN;
    const url = `${base}/api/chat/${conversationId}/read-global`;

    const res = await authenticatedRequest(url, {
      rawResponse: true,
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: { seenAt: Date.now() },
      diagSource: 'ChatWindow.markConversationRead',
    });

    if (!res.ok) {
      // Log suave para debug (404 en local, auth issues, etc) sin romper UX
      console.warn(
        '[ChatWindow] markConversationRead failed',
        res.status,
        res.statusText,
        url
      );
    }
  } catch (err) {
    // Silencioso: si falla no rompemos UX, pero dejamos una pista en consola dev
    console.warn('[ChatWindow] markConversationRead error', err && err.message);
  }
}

function readActiveConv() {
  try { return localStorage.getItem('z10_active_conv') || null; } catch { return null; }
}
function getLastReadTs(convId) {
  if (!convId) return 0;
  try {
    const m = JSON.parse(localStorage.getItem('z10_lastread') || '{}') || {};
    const v = m[String(convId)];
    return typeof v === 'number' ? v : 0;
  } catch {
    return 0;
  }
}

// === Unread notifications (simple cross-view store) ===
const UNREAD_LS_KEY = 'z10_unread';
function readUnread() {
  try { return JSON.parse(localStorage.getItem(UNREAD_LS_KEY) || '{}') || {}; } catch { return {}; }
}
function writeUnread(map) {
  try {
    localStorage.setItem(UNREAD_LS_KEY, JSON.stringify(map || {}));
    // notify other views (ContactsSidebar, headers, etc.)
    window.dispatchEvent(new CustomEvent('z10:unread-changed', { detail: { unread: map || {} } }));
  } catch {}
}
function incUnread(convId, tsMs) {
  if (!convId) return;
  const active = readActiveConv();
  if (String(active) === String(convId)) return;
  const lastTs = getLastReadTs(convId);
  const msgTs = typeof tsMs === 'number' ? tsMs : 0;
  if (msgTs && lastTs && msgTs <= lastTs) return;
  const m = readUnread();
  m[String(convId)] = (m[String(convId)] || 0) + 1;
  writeUnread(m);
}
function clearUnread(convId) {
  if (!convId) return;
  const m = readUnread();
  if (m[String(convId)]) {
    delete m[String(convId)];
    writeUnread(m);
  }
}

// === Active conversation + lastRead tracking ===
const ACTIVE_CONV_LS_KEY = 'z10_active_conv';
const LAST_READ_LS_KEY = 'z10_lastread';
function setActiveConv(convId) {
  try {
    if (convId) {
      localStorage.setItem(ACTIVE_CONV_LS_KEY, String(convId));
    } else {
      localStorage.removeItem(ACTIVE_CONV_LS_KEY);
    }
    window.dispatchEvent(new CustomEvent('z10:active-conv-changed', { detail: { convId } }));
  } catch {}
}
function clearActiveConv() {
  setActiveConv(null);
}
function readLastRead() {
  try { return JSON.parse(localStorage.getItem(LAST_READ_LS_KEY) || '{}') || {}; } catch { return {}; }
}
function writeLastRead(map) {
  try {
    localStorage.setItem(LAST_READ_LS_KEY, JSON.stringify(map || {}));
    window.dispatchEvent(new CustomEvent('z10:lastread-changed', { detail: { lastRead: map || {} } }));
  } catch {}
}
function bumpLastRead(convId, ts) {
  if (!convId) return;
  const m = readLastRead();
  const prev = m[String(convId)] || 0;
  // Only bump if ts is newer or not set
  if (!ts) ts = Date.now();
  if (!prev || ts > prev) {
    m[String(convId)] = ts;
    writeLastRead(m);
  }
}


// Utilidad: fecha dd/mm/yyyy + hora hh:mm
function formatDateTime(dateLike) {
  try {
    const d = new Date(dateLike);
    const dd = String(d.getDate()).padStart(2, '0');
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const yyyy = d.getFullYear();
    const hh = String(d.getHours()).padStart(2, '0');
    const min = String(d.getMinutes()).padStart(2, '0');
    return `${dd}/${mm}/${yyyy} · ${hh}:${min}`;
  } catch {
    return '';
  }
}

// Helper: zodiac sign to emoji
function signToEmoji(sign) {
  if (!sign) return '';
  const s = String(sign).trim().toLowerCase();
  const map = {
    // Spanish
    'aries': '♈️', 'tauro': '♉️', 'geminis': '♊️', 'géminis': '♊️', 'cancer': '♋️', 'cáncer': '♋️',
    'leo': '♌️', 'virgo': '♍️', 'libra': '♎️', 'escorpio': '♏️', 'sagitario': '♐️',
    'capricornio': '♑️', 'acuario': '♒️', 'piscis': '♓️',
    // English
    'aries_en': '♈️', 'taurus': '♉️', 'gemini': '♊️', 'cancer_en': '♋️', 'leo_en': '♌️',
    'virgo_en': '♍️', 'libra_en': '♎️', 'scorpio': '♏️', 'sagittarius': '♐️',
    'capricorn': '♑️', 'aquarius': '♒️', 'pisces': '♓️',
  };
  // Map common english names to *_en keys to avoid clash with spanish keys like "cancer"
  const englishAliases = {
    'aries': 'aries_en',
    'leo': 'leo_en',
    'cancer': 'cancer_en',
  };
  const key = map[s] ? s : (map[englishAliases[s]] ? englishAliases[s] : s);
  return map[key] || '';
}

// Normaliza URLs de foto (si viene relativa del backend)
const API_BASE = REST_API_ORIGIN;
function photoURL(p) {
  // Prefer inline default to avoid 404s in local dev if the file isn't in /public
  if (!p) return DEFAULT_AVATAR_DATA;
  const s = String(p);
  if (/^https?:\/\//i.test(s)) return s;
  return `${API_BASE}/${s.replace(/^\/+/, '')}`;
}

// Default avatar and preloader to avoid broken-image flash on mobile
//const DEFAULT_AVATAR = '/default-profile.png';
const DEFAULT_AVATAR_DATA = 'data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" width="80" height="80" viewBox="0 0 80 80"><defs><linearGradient id="g" x1="0" y1="0" x2="1" y2="1"><stop offset="0%" stop-color="%23DAA520" stop-opacity="0.9"/><stop offset="100%" stop-color="%234B0082" stop-opacity="0.9"/></linearGradient></defs><circle cx="40" cy="40" r="40" fill="url(%23g)"/><circle cx="40" cy="36" r="16" fill="white" fill-opacity="0.85"/><rect x="12" y="54" width="56" height="20" rx="10" ry="10" fill="white" fill-opacity="0.85"/></svg>';
function preloadImage(src) {
  return new Promise((resolve, reject) => {
    const img = new window.Image();
    img.decoding = 'async';
    img.onload = () => resolve(src);
    img.onerror = reject;
    img.src = src;
  });
}

export default function ChatWindow({ conversationId: propConversationId, me }) {
  const params = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const conversationId = propConversationId || params.conversationId;

  // Fix mobile 100vh issue (must run inside a component)
  useEffect(() => {
    const setVH = () => {
      if (typeof window !== 'undefined' && window.visualViewport) {
        document.documentElement.style.setProperty('--vh', `${window.visualViewport.height * 0.01}px`);
      } else if (typeof window !== 'undefined') {
        // Fallback for older browsers without visualViewport
        const h = window.innerHeight || document.documentElement.clientHeight || 0;
        document.documentElement.style.setProperty('--vh', `${h * 0.01}px`);
      }
    };
    setVH();
    window.visualViewport?.addEventListener('resize', setVH);
    window.addEventListener('resize', setVH);
    return () => {
      window.visualViewport?.removeEventListener('resize', setVH);
      window.removeEventListener('resize', setVH);
    };
  }, []);

  // Fallback: si no me pasan "me" por props, lo levanto del localStorage
  const meLocal = React.useMemo(() => {
    try {
      return JSON.parse(localStorage.getItem('me') || 'null');
    } catch {
      return null;
    }
  }, []);

  // Extra fallbacks (algunos navegadores/flows guardan en otras keys)
  const lsUserId = React.useMemo(() => {
    try {
      const v = localStorage.getItem('userId');
      return v && v !== 'undefined' ? v : null;
    } catch { return null; }
  }, []);

  const lsUserObj = React.useMemo(() => {
    try {
      const raw = localStorage.getItem('user');
      return raw ? JSON.parse(raw) : null;
    } catch { return null; }
  }, []);

  const [messages, setMessages] = useState([]);
  const [draft, setDraft] = useState('');
  const [typingUsers, setTypingUsers] = useState(new Set());
  const [showEmoji, setShowEmoji] = useState(false);
  const [loading, setLoading] = useState(true);

  // Header state (solo peer)
  const [peerName, setPeerName] = useState('Contacto');
  const [peerPhoto, setPeerPhoto] = useState('');
  const [peerSign, setPeerSign] = useState('');
  const [resolvedPeerPhoto, setResolvedPeerPhoto] = useState(DEFAULT_AVATAR_DATA);
  // Presence state for peer
  const [peerUserId, setPeerUserId] = useState(null);

  // If ContactsSidebar navigates with peerUserId in state, use it immediately (faster header presence)
  useEffect(() => {
    try {
      const st = location && location.state;
      const pid = st && (st.peerUserId || st.peerId || st.userId);
      if (pid) {
        setPeerUserId((prev) => prev || String(pid));
        if (!peerIdRef.current) peerIdRef.current = String(pid);
      }
    } catch {}
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location]);

  const [peerOnline, setPeerOnline] = useState(false);
  const [peerLastSeen, setPeerLastSeen] = useState(null);
  // Guardamos el último momento en que supimos que estuvo activo (evita closures viejos)
  const peerLastOnlineAtRef = useRef(0);
  // (presence freshness ticker removed to avoid unused state warnings)
  // Preload peer avatar to avoid broken-image flash on mobile
  useEffect(() => {
    const url = peerPhoto ? photoURL(peerPhoto) : DEFAULT_AVATAR_DATA;
    if (!peerPhoto) {
      setResolvedPeerPhoto(DEFAULT_AVATAR_DATA);
      return;
    }
    let cancelled = false;
    preloadImage(url)
      .then(() => { if (!cancelled) setResolvedPeerPhoto(url); })
      .catch(() => { if (!cancelled) setResolvedPeerPhoto(DEFAULT_AVATAR_DATA); });
    return () => { cancelled = true; };
  }, [peerPhoto]);


// === GLOBAL presence (igual que ContactsSidebar) ===
useEffect(() => {
  if (!peerUserId) return;

  const uid = String(peerUserId);

  const parseLastSeen = (val) => {
    if (!val) return null;
    // number could be seconds or ms
    if (typeof val === 'number' && Number.isFinite(val)) {
      return val < 1e12 ? val * 1000 : val;
    }
    // ISO/date string
    if (typeof val === 'string') {
      const t = Date.parse(val);
      return Number.isFinite(t) ? t : null;
    }
    return null;
  };

  const extractLastSeen = (p) => {
    // tolerate multiple back-end keys
    const raw =
      p?.lastSeen ??
      p?.lastSeenAt ??
      p?.last_seen ??
      p?.seenAt ??
      p?.updatedAt ??
      p?.ts ??
      null;
    return parseLastSeen(raw);
  };

  const apply = (p) => {
    if (p == null) return;

    const appliedAt = Date.now();

    // tolerate different server shapes: boolean, {online}, {isOnline}, {connected}, {status}, etc.
    const onlineVal =
      (typeof p === 'boolean' ? p : undefined) ??
      (typeof p?.online === 'boolean' ? p.online : undefined) ??
      (typeof p?.isOnline === 'boolean' ? p.isOnline : undefined) ??
      (typeof p?.connected === 'boolean' ? p.connected : undefined) ??
      (typeof p?.status === 'string'
        ? (String(p.status).toLowerCase() === 'online')
        : undefined);

    const lastSeenMs = extractLastSeen(p);

    if (typeof onlineVal === 'boolean') {
      setPeerOnline(onlineVal);

      if (onlineVal) {
        // Última evidencia de actividad real
        peerLastOnlineAtRef.current = Math.max(peerLastOnlineAtRef.current || 0, appliedAt);
        // Si está activo, no mostramos lastSeen (mostramos estado “Activo ahora”)
        setPeerLastSeen(null);
      } else {
        // Offline: only trust an explicit lastSeen from the backend.
        // Never recycle previous/fallback timestamps because they create fake
        // labels like "Activo hace unos segundos" even when there is no real
        // last offline timestamp available.
        setPeerLastSeen(lastSeenMs || null);
      }
      return;
    }

    // Si el backend manda sólo timestamps sin boolean de online
    if (lastSeenMs) {
      setPeerLastSeen(lastSeenMs);
      peerLastOnlineAtRef.current = Math.max(peerLastOnlineAtRef.current || 0, lastSeenMs);
    }
  };

  // Snapshot inicial
  (async () => {
    try {
      const map = await queryUsersPresence([uid]);
      apply(map?.[uid]);
    } catch {}
  })();

  // Updates realtime
  const unsub = onUserPresence((p) => {
    if (String(p?.userId) !== uid) return;
    apply(p);
  });

  return () => {
    try { unsub && unsub(); } catch {}
  };
// eslint-disable-next-line react-hooks/exhaustive-deps
}, [peerUserId]);

  const bottomRef = useRef(null);
  const inputRef = useRef(null);

  // Keep latest messages for effect cleanup (avoids stale closure)
  const messagesRef = useRef([]);
  useEffect(() => {
    messagesRef.current = messages;
  }, [messages]);

  const token = getAuthToken() || null;
  const myUserId =
    me?.userId || me?._id || me?.id ||
    meLocal?.userId || meLocal?._id || meLocal?.id ||
    lsUserObj?._id || lsUserObj?.id || lsUserId ||
    null;

  const myName =
    me?.fullName || meLocal?.fullName || lsUserObj?.fullName || lsUserObj?.name || 'Yo';


  const peerIdRef = useRef(null);
  const peerNameRef = useRef('Contacto');
  const peerPhotoRef = useRef('');
  // 🔒 Lock para no volver a pisar el header una vez resuelto
  const headerLockedRef = useRef(false);
  // Evitar pedir varias veces el perfil
  const triedPeerFetchRef = useRef(false);
  const sendingRef = useRef(false);
  const [isSending, setIsSending] = useState(false);

  // helper: agrega mensaje solo si no existe (soporta _id o id; fallback a hash)
  const addMessageUnique = (msg) => {
    if (!msg) return;
    // Normalizar id de conversacion (string u objeto con _id)
    const convKey = String(
      (msg.conversation && typeof msg.conversation === 'object' && (msg.conversation._id || msg.conversation.id)) ||
      msg.conversation ||
      conversationId ||
      ''
    );

    // Construir una clave estable aunque no venga _id
    const senderKey = String(
      (msg.sender && typeof msg.sender === 'object' && (msg.sender._id || msg.sender.id)) ||
      msg.sender ||
      'unknown'
    );
    const when = String(msg.createdAt || msg.timestamp || msg.created_at || '');
    const body = String(msg.text ?? msg.content ?? msg.body ?? '').slice(0, 64);
    const key =
      String(msg._id || msg.id || msg.tempId || '') ||
      `${convKey}|${senderKey}|${when}|${body}`;

    setMessages((prev) => {
      if (!Array.isArray(prev)) return [msg];
      if (prev.some((m) => {
        const mk = String(
          (m && (m._id || m.id || m.tempId)) ||
          `${String(
            (m?.conversation && typeof m.conversation === 'object' && (m.conversation._id || m.conversation.id)) ||
            m?.conversation ||
            conversationId ||
            ''
          )}|${String(
            (m?.sender && typeof m.sender === 'object' && (m.sender._id || m.sender.id)) ||
            m?.sender ||
            'unknown'
          )}|${String(m?.createdAt || m?.timestamp || m?.created_at || '')}|${String(m?.text ?? m?.content ?? m?.body ?? '').slice(0, 64)}`
        );
        return mk === key;
      })) return prev;
      return [...prev, msg];
    });
  };

  // Auto-scroll al último mensaje
  useEffect(() => {
    if (!bottomRef.current) return;
    bottomRef.current.scrollIntoView({ behavior: 'smooth', block: 'end' });
  }, [messages.length]);


  // Si ya conozco el peerId pero aún no tengo foto/nombre, traigo el perfil una sola vez
  useEffect(() => {
    if (!peerIdRef.current || headerLockedRef.current || triedPeerFetchRef.current) return;
    triedPeerFetchRef.current = true;
    // fetchPeerProfile disabled (we rely on conversation info and message sender snapshots)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [conversationId]);

  // Carga/escucha de conversación
  useEffect(() => {
    if (!conversationId) return;
    setLoading(true);
    // --- Active conv & lastRead setup ---
    setActiveConv(conversationId);
    clearUnread(conversationId);
    bumpLastRead(conversationId, Date.now());

    // --- 1) Registrar listeners ANTES de unirme a la sala ---
    const handleNew = (msg) => {
      const msgConvId = String(
        (msg?.conversation && typeof msg.conversation === 'object' && (msg.conversation._id || msg.conversation.id)) ||
        msg?.conversation
      );
      if (msgConvId !== String(conversationId)) {
  const senderId = msg?.sender && (typeof msg.sender === 'object' ? (msg.sender._id || msg.sender.id) : msg.sender);
  const ts = new Date(msg?.createdAt || msg?.timestamp || msg?.created_at || Date.now()).getTime();
  if (senderId && String(senderId) !== String(myUserId)) {
    incUnread(msg.conversation, ts);
  }
  return;
}

      const rawSender = msg?.sender;
      let norm = { ...msg };

      // Normalizo sender + no piso header si ya está “lockeado”
      if (rawSender && (typeof rawSender === 'string' || typeof rawSender === 'number')) {
        const senderId = String(rawSender);
        if (senderId === String(myUserId)) {
          norm.sender = { _id: senderId, fullName: myName };
        } else {
          if (!peerIdRef.current) peerIdRef.current = senderId;
setPeerUserId(prev => prev || senderId);
          const fallback = peerNameRef.current || 'Contacto';
          norm.sender = { _id: senderId, fullName: fallback };
          // fetchPeerProfile disabled
        }
      } else if (rawSender && typeof rawSender === 'object' && rawSender._id) {
        const senderId = String(rawSender._id);
        if (senderId !== String(myUserId)) {
          if (!peerIdRef.current) peerIdRef.current = senderId;
setPeerUserId(prev => prev || senderId);
          // header hydration from sender object
        }
      }

      // Hydrate header fields from sender snapshot if present
      // ⚠️ Importante: NO pisar el signo/nombre del peer con datos del usuario actual.
      if (rawSender && typeof rawSender === 'object') {
        const senderIdObj = String(rawSender._id || rawSender.id || rawSender.userId || '');
        const isMineSender = senderIdObj && myUserId && String(senderIdObj) === String(myUserId);

        if (!isMineSender) {
          // Lock only protects NAME/PHOTO. Bio and sign can still hydrate later.
          if (!headerLockedRef.current) {
            if (rawSender.fullName && (!peerNameRef.current || peerNameRef.current === 'Contacto')) {
              peerNameRef.current = rawSender.fullName;
              setPeerName(rawSender.fullName);
            }
            if (rawSender.photoPath && !peerPhotoRef.current) {
              peerPhotoRef.current = rawSender.photoPath;
              setPeerPhoto(rawSender.photoPath);
            }
          }
          // Sign del peer: sólo si el sender NO soy yo (evita que cambie a tu signo al tipear/enviar)
          if (rawSender.zodiacSign) {
            setPeerSign((prev) => (prev ? prev : rawSender.zodiacSign));
          }
        }
      }

      if (!norm._id && norm.id) { norm._id = norm.id; }
      addMessageUnique(norm);
    };

    try {
      const nowTs = Date.now();
      bumpLastRead(conversationId, nowTs);
      clearUnread(conversationId);
    } catch {}

    const handleTyping = ({ conversationId: cid, userId }) => {
      if (String(cid) !== String(conversationId) || String(userId) === String(myUserId)) return;
      setTypingUsers((prev) => new Set([...prev, userId]));
    };

    const handleStop = ({ conversationId: cid, userId }) => {
      if (String(cid) !== String(conversationId) || String(userId) === String(myUserId)) return;
      setTypingUsers((prev) => {
        const s = new Set(prev);
        s.delete(userId);
        return s;
      });
    };


    const unsubscribeNew = onNewConversationMessage(handleNew);
    const unsubscribeTyping = onTyping(handleTyping);
    const unsubscribeStopTyping = onStopTyping(handleStop);
    

    // --- 2) Unirse a la conversación (necesario para recibir realtime) ---
    joinConversation({ conversationId, userId: myUserId });
    void requestTypingSnapshot(conversationId);
    setActiveConv(conversationId);
    clearUnread(conversationId);
    bumpLastRead(conversationId, Date.now());

    // --- 2.1) Obtener info del peer (nombre/foto) sin depender de mensajes ---
    (async () => {
      try {
        const info = await getConversationInfo(token, conversationId);
        const peer = info?.peer;
        if (peer && peer._id) {
          peerIdRef.current = String(peer._id);
          setPeerUserId((prev) => prev || String(peer._id));

          // Nombre y foto estables del peer
          const stableName = peer.fullName || peer.name || peer.email || 'Contacto';
          const stablePhoto = peer.photoPath || '';
          const stableSign = peer.zodiacSign || '';

          // Set stable name/photo and lock only those two.
          peerNameRef.current = stableName;
          setPeerName(stableName);

          if (stablePhoto) {
            peerPhotoRef.current = stablePhoto;
            setPeerPhoto(stablePhoto);
          }

          if (stableSign) setPeerSign(stableSign);

          // Lock header for name/photo only.
          headerLockedRef.current = true;
          triedPeerFetchRef.current = true;
        }
      } catch {
        // fetchPeerProfile disabled (we rely on conversation info and message sender snapshots)
      }
    })();

    // --- 3) Cargar historial (sirve para deducir peer si no hay nadie online) ---
    (async () => {
      try {
        const resp = await getConversationMessages(token, conversationId);
        // Admitimos varias formas de respuesta: Array directo, {messages: [...]}, {data:{messages: [...]}}
        let msgs = [];
        if (Array.isArray(resp)) {
          msgs = resp;
        } else if (resp && Array.isArray(resp.messages)) {
          msgs = resp.messages;
        } else if (resp && resp.data && Array.isArray(resp.data.messages)) {
          msgs = resp.data.messages;
        } else {
          // Log no intrusivo para diagnóstico (una sola vez por montaje)
          console.log('[ChatWindow] unexpected messages response shape:', resp);
        }

        // Ordenar por fecha asc para que queden cronológicos si vienen mezclados
        try {
          msgs = [...msgs].sort((a, b) => new Date(a.createdAt || a.timestamp || 0) - new Date(b.createdAt || b.timestamp || 0));
        } catch {}

        const other = msgs.find((m) => {
          const sid = m?.sender?._id || m?.sender;
          return sid && String(sid) !== String(myUserId);
        });

        if (other) {
          const sid = String(other?.sender?._id || other?.sender);
          if (sid) {
            if (!peerIdRef.current) peerIdRef.current = sid;
            setPeerUserId((prev) => prev || sid);

            if (!headerLockedRef.current) {
              if (other.sender && typeof other.sender === 'object') {
                const n = other.sender.fullName || other.sender.name || other.sender.email || 'Contacto';
                const ph = other.sender.photoPath || '';
                peerNameRef.current = n;
                setPeerName(n);
                if (ph) {
                  peerPhotoRef.current = ph;
                  setPeerPhoto(ph);
                }
                if (other.sender && typeof other.sender === 'object') {
                  if (other.sender.zodiacSign && !peerSign) {
                    setPeerSign(other.sender.zodiacSign);
                  }
                }
                headerLockedRef.current = true;
              }
            }
          }
        }

        setMessages(msgs);
        // Compute lastTs and bump lastRead/clearUnread
        let lastTs = 0;
        if (msgs.length > 0) {
          const lastMsg = msgs[msgs.length - 1];
          lastTs = new Date(lastMsg.createdAt || lastMsg.timestamp || 0).getTime();
        }
        bumpLastRead(conversationId, lastTs || Date.now());
        clearUnread(conversationId);
        // Avisar al backend que esta conversación quedó leída globalmente
        try {
          markConversationRead(token, conversationId);
        } catch {}
        // Aseguramos el primer scroll cuando llegan
        try {
          requestAnimationFrame(() => {
            bottomRef.current?.scrollIntoView({ behavior: 'auto', block: 'end' });
          });
        } catch {}
      } catch (e) {
        console.log('[ChatWindow] history error:', e.message || e);
      } finally {
        setLoading(false);
      }
    })();

    return () => {
      bumpLastRead(conversationId, Date.now());
      clearTypingConversation(conversationId);
      clearActiveConv();
      leaveConversation({ conversationId, userId: myUserId });
      try {
        let lastTs = Date.now();
        const msgs = messagesRef.current;
        if (msgs && msgs.length > 0) {
          const lastMsg = msgs[msgs.length - 1];
          lastTs = new Date(lastMsg.createdAt || lastMsg.timestamp || 0).getTime() || Date.now();
        }
        bumpLastRead(conversationId, lastTs);
        clearUnread(conversationId);
      } catch {}

      try { unsubscribeNew && unsubscribeNew(); } catch {}
      try { unsubscribeTyping && unsubscribeTyping(); } catch {}
      try { unsubscribeStopTyping && unsubscribeStopTyping(); } catch {}
      
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [conversationId, myUserId]);

  // Emoji seleccionado (cierra el picker y devuelve el foco al input)
  const onEmojiClick = (emojiData) => {
    setDraft((prev) => prev + (emojiData?.emoji || ''));
    setShowEmoji(false);
    setTimeout(() => inputRef.current?.focus(), 0);
  };

  const emojiBtnRef = useRef(null);
  const emojiPopupRef = useRef(null);
  
  useEffect(() => {
  if (!showEmoji) return;
  const onDocClick = (e) => {
    const t = e.target;
    const inBtn = emojiBtnRef.current && emojiBtnRef.current.contains(t);
    const inPopup = emojiPopupRef.current && emojiPopupRef.current.contains(t);
    if (!inBtn && !inPopup) setShowEmoji(false);
  };
  const onEsc = (e) => { if (e.key === 'Escape') setShowEmoji(false); };
  document.addEventListener('click', onDocClick, true);
  document.addEventListener('keydown', onEsc, true);
  return () => {
    document.removeEventListener('click', onDocClick, true);
    document.removeEventListener('keydown', onEsc, true);
  };
}, [showEmoji]);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (sendingRef.current) return;
    const text = draft.trim();
    if (!text) return;
    sendingRef.current = true;
    setIsSending(true);
    stopTyping(conversationId);
    try {
      const saved = await sendMessage(token, conversationId, { text }); // guarda en DB (REST)
      if (saved && !saved._id && saved.id) { saved._id = saved.id; }
      addMessageUnique(saved);
      bumpLastRead(conversationId, new Date(saved.createdAt || saved.timestamp || Date.now()).getTime());
      clearUnread(conversationId);
      setDraft('');
      setShowEmoji(false);
    } catch (err) {
      console.error('sendMessage error:', err);
      // Avoid blocking alert() in mobile; keep UX smooth
      console.warn('No se pudo enviar el mensaje');
    } finally {
      sendingRef.current = false;
      setIsSending(false);
    }
  };

  const onChange = (e) => {
    const nextDraft = e.target.value;
    setDraft(nextDraft);
    if (nextDraft.trim()) startTyping(conversationId);
    else stopTyping(conversationId);
  };

  // ======= Responsive helpers =======
  const [isMobile, setIsMobile] = useState(() => {
    if (typeof window === 'undefined') return false;
    return window.matchMedia('(max-width: 600px)').matches;
  });

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const mq = window.matchMedia('(max-width: 600px)');
    const handler = (e) => setIsMobile(e.matches);
    if (mq.addEventListener) mq.addEventListener('change', handler);
    else mq.addListener(handler);
    return () => {
      if (mq.removeEventListener) mq.removeEventListener('change', handler);
      else mq.removeListener(handler);
    };
  }, []);

  // ====== Keyboard detection (mobile) ======
  const [kb, setKb] = useState({ open: false, height: 0 });
  useEffect(() => {
    if (typeof window === 'undefined' || !window.visualViewport) return;
    const update = () => {
      const vh = window.visualViewport.height;
      const wh = window.innerHeight;
      // Heuristic: keyboard is open if visualViewport.height < window.innerHeight - 80
      const open = isMobile && vh < wh - 80;
      const height = open ? wh - vh : 0;
      setKb({ open, height });
    };
    window.visualViewport.addEventListener('resize', update);
    window.addEventListener('orientationchange', update);
    update();
    return () => {
      window.visualViewport.removeEventListener('resize', update);
      window.removeEventListener('orientationchange', update);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isMobile]);

const composerH = isMobile ? 54 : 70;
const safeBottom = 'env(safe-area-inset-bottom)';
const outerPad = isMobile ? 8 : 16;

  // ======= ESTILOS (con status online) =======
  const styles = {
    page: {
      minHeight: '100dvh',
      height: '100dvh',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'flex-start',
      padding: isMobile ? 0 : outerPad,
      color: '#F8F1FF',
      overflow: 'hidden',
      background:
        'radial-gradient(circle at 18% 8%, rgba(255,215,0,0.12), transparent 24%), radial-gradient(circle at 86% 16%, rgba(190,80,255,0.22), transparent 30%), radial-gradient(circle at 50% 72%, rgba(75,0,130,0.38), transparent 58%), linear-gradient(180deg, #070014 0%, #120026 48%, #050008 100%)',
      boxSizing: 'border-box',
    },
    card: {
      position: 'relative',
      width: '100%',
      maxWidth: isMobile ? '100%' : 820,
      height: isMobile ? '100dvh' : `calc(100dvh - ${outerPad * 2}px)`,
      background: 'linear-gradient(180deg, rgba(9,0,24,0.96) 0%, rgba(18,0,42,0.94) 100%)',
      borderRadius: isMobile ? 0 : 26,
      boxShadow: isMobile ? 'none' : '0 28px 78px rgba(0,0,0,0.48), 0 0 46px rgba(170,64,255,0.16)',
      display: 'flex',
      flexDirection: 'column',
      border: isMobile ? 'none' : '1px solid rgba(218,165,32,0.24)',
      backdropFilter: 'blur(16px) saturate(1.12)',
      WebkitBackdropFilter: 'blur(16px) saturate(1.12)',
      overflow: 'hidden',
      isolation: 'isolate',
    },
    header: {
      position: 'sticky',
      top: 0,
      zIndex: 20,
      display: 'flex',
      alignItems: 'center',
      gap: isMobile ? 12 : 14,
      paddingTop: `calc(${isMobile ? 14 : 14}px + env(safe-area-inset-top, 0px))`,
      paddingRight: isMobile ? 14 : 18,
      paddingBottom: isMobile ? 16 : 16,
      paddingLeft: isMobile ? 14 : 18,
      minHeight: isMobile ? 106 : 96,
      background:
        'linear-gradient(135deg, rgba(75,0,130,0.92), rgba(28,2,62,0.94) 58%, rgba(9,0,24,0.98))',
      color: '#FFFFFF',
      boxShadow: '0 18px 42px rgba(0,0,0,0.34), inset 0 1px 0 rgba(255,255,255,0.08)',
      backdropFilter: 'blur(16px) saturate(1.15)',
      WebkitBackdropFilter: 'blur(16px) saturate(1.15)',
      borderBottom: '1px solid rgba(218,165,32,0.22)',
      flexShrink: 0,
    },
    backBtn: {
      border: '1px solid rgba(255,255,255,0.14)',
      background: 'linear-gradient(145deg, rgba(255,255,255,0.12), rgba(138,43,226,0.18))',
      color: '#FFF6C8',
      padding: 0,
      width: isMobile ? 54 : 48,
      height: isMobile ? 54 : 48,
      borderRadius: 18,
      cursor: 'pointer',
      touchAction: 'manipulation',
      WebkitTapHighlightColor: 'transparent',
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      fontWeight: 950,
      fontSize: 22,
      boxShadow: '0 12px 26px rgba(0,0,0,0.32), inset 0 1px 0 rgba(255,255,255,0.10)',
      backdropFilter: 'blur(12px)',
      WebkitBackdropFilter: 'blur(12px)',
      flexShrink: 0,
    },
    titleWrap: { display: 'flex', flexDirection: 'column', minWidth: 0, flex: 1 },
    title: {
      margin: 0,
      fontSize: isMobile ? 22 : 20,
      fontWeight: 950,
      letterSpacing: '-0.02em',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
      maxWidth: isMobile ? '48vw' : 520,
      color: '#FFFFFF',
      textShadow: '0 0 18px rgba(255,255,255,0.12)',
    },
    nameRow: { display: 'flex', alignItems: 'center', gap: 8, minWidth: 0, flexWrap: 'nowrap' },
    statusRow: {
      display: 'flex',
      alignItems: 'center',
      gap: 8,
      marginTop: 3,
      minHeight: 16,
    },
    statusText: {
      fontSize: isMobile ? 12 : 12,
      opacity: 0.9,
      color: 'rgba(248,241,255,0.88)',
      letterSpacing: 0.1,
      whiteSpace: 'nowrap',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      maxWidth: isMobile ? '50vw' : 520,
    },
    statusPill: {
      display: 'inline-flex',
      alignItems: 'center',
      gap: 8,
      padding: '6px 12px',
      borderRadius: 999,
      border: '1px solid rgba(218,165,32,0.30)',
      background: 'linear-gradient(145deg, rgba(255,255,255,0.10), rgba(138,43,226,0.16))',
      boxShadow: '0 8px 18px rgba(0,0,0,0.22), inset 0 1px 0 rgba(255,255,255,0.08)',
      backdropFilter: 'blur(12px)',
      WebkitBackdropFilter: 'blur(12px)',
    },
    list: {
      position: 'relative',
      paddingTop: isMobile ? 18 : 22,
      paddingRight: isMobile ? 14 : 18,
      paddingLeft: isMobile ? 14 : 18,
      paddingBottom: isMobile ? 8 : 10,
      flex: 1,
      minHeight: 0,
      overflowY: 'auto',
      WebkitOverflowScrolling: 'touch',
      background:
        'radial-gradient(circle at 50% 45%, rgba(218,165,32,0.08), transparent 34%), radial-gradient(circle at 52% 58%, rgba(138,43,226,0.18), transparent 52%), linear-gradient(180deg, rgba(10,0,28,0.96), rgba(5,0,16,0.98))',
      backgroundAttachment: 'local',
      overscrollBehaviorY: 'contain',
    },
    listLoading: {
      position: 'relative',
      paddingTop: isMobile ? 18 : 22,
      paddingRight: isMobile ? 14 : 18,
      paddingLeft: isMobile ? 14 : 18,
      paddingBottom: isMobile ? 10 : 12,
      flex: 1,
      minHeight: 0,
      overflow: 'hidden',
      background:
        'radial-gradient(circle at 50% 45%, rgba(218,165,32,0.08), transparent 34%), radial-gradient(circle at 52% 58%, rgba(138,43,226,0.18), transparent 52%), linear-gradient(180deg, rgba(10,0,28,0.96), rgba(5,0,16,0.98))',
    },
    skeletonRow: (alignRight) => ({
      display: 'flex',
      justifyContent: alignRight ? 'flex-end' : 'flex-start',
      marginBottom: isMobile ? 10 : 12,
    }),
    skeletonBubble: (alignRight) => ({
      width: isMobile ? '78%' : '62%',
      height: isMobile ? 52 : 46,
      borderRadius: 18,
      borderTopLeftRadius: alignRight ? 18 : 8,
      borderTopRightRadius: alignRight ? 8 : 18,
      background: alignRight
        ? 'linear-gradient(90deg, rgba(218,165,32,0.34), rgba(255,255,255,0.10), rgba(218,165,32,0.30))'
        : 'linear-gradient(90deg, rgba(138,43,226,0.28), rgba(255,255,255,0.10), rgba(138,43,226,0.24))',
      border: alignRight ? '1px solid rgba(218,165,32,0.30)' : '1px solid rgba(190,120,255,0.22)',
      backgroundSize: '200% 100%',
      animation: 'zdx-shimmer 1.1s linear infinite',
    }),
    bubbleRow: (isMine) => ({
      display: 'flex',
      justifyContent: isMine ? 'flex-end' : 'flex-start',
      marginBottom: isMobile ? 10 : 12,
      paddingLeft: isMobile ? 2 : 0,
      paddingRight: isMobile ? 2 : 0,
    }),
    bubble: (isMine) => ({
      maxWidth: isMobile ? '82%' : '72%',
      minWidth: 0,
      padding: isMobile ? '13px 14px' : '12px 14px',
      borderRadius: 22,
      borderTopLeftRadius: isMine ? 22 : 8,
      borderTopRightRadius: isMine ? 8 : 22,
      background: isMine
        ? 'linear-gradient(145deg, rgba(218,165,32,0.28), rgba(93,61,22,0.74))'
        : 'linear-gradient(145deg, rgba(48,15,92,0.86), rgba(20,0,47,0.88))',
      border: isMine
        ? '1px solid rgba(218,165,32,0.78)'
        : '1px solid rgba(190,120,255,0.28)',
      color: '#F8F1FF',
      boxShadow: isMine
        ? '0 14px 30px rgba(0,0,0,0.28), 0 0 18px rgba(218,165,32,0.12), inset 0 1px 0 rgba(255,255,255,0.08)'
        : '0 14px 30px rgba(0,0,0,0.28), 0 0 18px rgba(170,64,255,0.08), inset 0 1px 0 rgba(255,255,255,0.07)',
      wordBreak: 'break-word',
      backdropFilter: 'blur(14px) saturate(1.08)',
      WebkitBackdropFilter: 'blur(14px) saturate(1.08)',
    }),
    name: {
      fontWeight: 950,
      fontSize: isMobile ? 12 : 12,
      color: '#FFFFFF',
      marginBottom: 7,
      letterSpacing: 0.1,
      textShadow: '0 0 12px rgba(255,255,255,0.08)',
    },
    signEmoji: {
      marginLeft: 6,
      fontSize: isMobile ? 14 : 13,
      verticalAlign: 'baseline',
      opacity: 0.9,
    },
    statusDot: {
      width: 10,
      height: 10,
      borderRadius: 999,
      flexShrink: 0,
      border: '1px solid rgba(255,255,255,0.55)',
      background: 'rgba(255,255,255,0.35)',
      boxShadow: '0 0 10px rgba(255,255,255,0.12)',
    },
    statusDotWrap: {
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      width: 18,
      height: 18,
      borderRadius: 999,
      background: 'rgba(255,255,255,0.10)',
      border: '1px solid rgba(255,255,255,0.16)',
      boxShadow: '0 6px 14px rgba(0,0,0,0.14)',
      backdropFilter: 'blur(10px)',
      WebkitBackdropFilter: 'blur(10px)',
    },
    text: {
      fontSize: isMobile ? 16 : 15,
      lineHeight: 1.45,
      whiteSpace: 'pre-wrap',
      overflowWrap: 'anywhere',
      wordBreak: 'break-word',
      color: '#FFFFFF',
    },
    time: {
      fontSize: isMobile ? 11 : 11,
      color: 'rgba(248,241,255,0.58)',
      marginTop: 8,
      textAlign: 'right',
      opacity: 0.95,
    },
    typing: {
      position: 'sticky',
      bottom: 0,
      background: 'transparent',
      padding: '0 14px 6px',
      fontStyle: 'italic',
      fontSize: 12,
      color: 'rgba(248,241,255,0.70)',
    },
    headerUser: { display: 'flex', alignItems: 'center', gap: isMobile ? 10 : 12, minWidth: 0, flex: 1 },
    headerAvatar: {
      width: isMobile ? 46 : 44,
      height: isMobile ? 46 : 44,
      borderRadius: '50%',
      border: '2px solid rgba(218,165,32,0.82)',
      backgroundColor: '#12002E',
      boxShadow: '0 10px 22px rgba(0,0,0,0.32), 0 0 0 3px rgba(218,165,32,0.12), 0 0 20px rgba(218,165,32,0.24)',
      flexShrink: 0,
      backgroundSize: 'cover',
      backgroundPosition: 'center',
      backgroundRepeat: 'no-repeat',
    },
    composerWrap: {
      position: 'sticky',
      bottom: 0,
      zIndex: 100,
      display: 'flex',
      alignItems: 'center',
      gap: isMobile ? 9 : 10,
      padding: isMobile ? '10px 12px' : '12px 14px',
      paddingBottom: `calc(${isMobile ? 14 : 14}px + ${safeBottom})`,
      background: 'linear-gradient(180deg, rgba(18,0,42,0.90), rgba(7,0,20,0.98))',
      borderTop: '1px solid rgba(218,165,32,0.20)',
      boxShadow: '0 -18px 42px rgba(0,0,0,0.36), inset 0 1px 0 rgba(255,255,255,0.06)',
      backdropFilter: 'blur(18px) saturate(1.1)',
      WebkitBackdropFilter: 'blur(18px) saturate(1.1)',
      flexShrink: 0,
    },
    emojiBtn: {
      border: '1px solid rgba(218,165,32,0.32)',
      background: 'linear-gradient(145deg, rgba(255,255,255,0.08), rgba(138,43,226,0.18))',
      color: '#F3C96B',
      fontSize: isMobile ? 23 : 22,
      lineHeight: 1,
      cursor: 'pointer',
      width: isMobile ? 44 : 42,
      height: isMobile ? 44 : 42,
      padding: 0,
      borderRadius: '50%',
      touchAction: 'manipulation',
      boxShadow: '0 10px 22px rgba(0,0,0,0.24), inset 0 1px 0 rgba(255,255,255,0.08)',
      flexShrink: 0,
    },
    emojiPopup: {
      position: 'absolute',
      left: 10,
      bottom: `calc(${composerH}px + 6px)`,
      zIndex: 30,
      boxShadow: '0 12px 30px rgba(0,0,0,0.25)',
      borderRadius: 12,
      overflow: 'hidden',
    },
    input: {
      flex: 1,
      minWidth: 0,
      padding: isMobile ? '13px 15px' : '13px 15px',
      borderRadius: 18,
      border: '1px solid rgba(190,120,255,0.32)',
      outline: 'none',
      fontSize: isMobile ? 16 : 15,
      background: 'rgba(7,0,22,0.58)',
      color: '#FFFFFF',
      boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.06), 0 0 18px rgba(170,64,255,0.08)',
      caretColor: '#F3C96B',
    },
    sendBtn: {
      border: 'none',
      padding: isMobile ? '13px 14px' : '13px 17px',
      minWidth: isMobile ? 74 : 86,
      borderRadius: 18,
      background: 'linear-gradient(135deg, #DAA520, #F7D774)',
      color: '#2B0048',
      fontWeight: 950,
      cursor: 'pointer',
      boxShadow: '0 12px 24px rgba(218,165,32,0.28), inset 0 1px 0 rgba(255,255,255,0.36)',
      transition: 'transform .08s ease, box-shadow .08s ease',
      touchAction: 'manipulation',
      flexShrink: 0,
    },
    srOnly: {
      position: 'absolute',
      width: 1,
      height: 1,
      padding: 0,
      margin: -1,
      overflow: 'hidden',
      clip: 'rect(0,0,0,0)',
      whiteSpace: 'nowrap',
      border: 0,
    },
  };

  const shimmerKeyframes = (
    <style>{`
      @keyframes zdx-shimmer {
        0% { background-position: 200% 0; }
        100% { background-position: -200% 0; }
      }
      input#chatMessage::placeholder {
        color: rgba(213,190,255,0.62);
      }
    `}</style>
  );

  // --- Update lastRead when messages length changes ---
  useEffect(() => {
    if (!conversationId) return;
    if (!messages || messages.length === 0) return;
    const lastMsg = messages[messages.length - 1];
    const lastTs = new Date(lastMsg.createdAt || lastMsg.timestamp || 0).getTime();
    bumpLastRead(conversationId, lastTs || Date.now());
    clearUnread(conversationId);
    // sync global read en el backend también cuando llega/alguien manda msg nuevo
    try {
      markConversationRead(token, conversationId);
    } catch {}
    // eslint-disable-next-line
  }, [messages.length, conversationId]);

  // --- Reconciliar unread para esta conversación ---
useEffect(() => {
  if (!conversationId) return;
  try {
    const lastTs = getLastReadTs(conversationId);
    if (!messages || messages.length === 0) {
      clearUnread(conversationId);
      return;
    }
    const count = messages.reduce((acc, m) => {
      const ts = new Date(m?.createdAt || m?.timestamp || 0).getTime();
      const senderId = m?.sender && (typeof m.sender === 'object' ? (m.sender._id || m.sender.id) : m.sender);
      const isMine = senderId && String(senderId) === String(myUserId);
      if (!isMine && ts && (!lastTs || ts > lastTs)) return acc + 1;
      return acc;
    }, 0);
    if (count > 0) {
      const m = readUnread();
      m[String(conversationId)] = count;
      writeUnread(m);
    } else {
      clearUnread(conversationId);
    }
  } catch {
    clearUnread(conversationId);
  }
}, [conversationId, messages, myUserId]);

  // --- Update lastRead on pagehide, beforeunload, visibilitychange (hidden) ---
  useEffect(() => {
    if (!conversationId) return;
    const handler = () => {
      // Use latest message timestamp from ref to avoid stale closures / listener churn
      let lastTs = Date.now();
      const msgs = messagesRef.current;
      if (msgs && msgs.length > 0) {
        const lastMsg = msgs[msgs.length - 1];
        lastTs = new Date(lastMsg.createdAt || lastMsg.timestamp || 0).getTime() || Date.now();
      }
      bumpLastRead(conversationId, lastTs);
    };
    const visHandler = () => {
      if (document.visibilityState === 'hidden') handler();
    };
    window.addEventListener('pagehide', handler);
    window.addEventListener('beforeunload', handler);
    document.addEventListener('visibilitychange', visHandler);
    return () => {
      window.removeEventListener('pagehide', handler);
      window.removeEventListener('beforeunload', handler);
      document.removeEventListener('visibilitychange', visHandler);
    };
  }, [conversationId]);

  return (
    <div style={styles.page}>
      <div style={styles.card}>
        {shimmerKeyframes}
        {/* Header */}
        <div style={styles.header}>
          <button
            style={styles.backBtn}
            onClick={() => navigate('/contacts')}
            aria-label={chatText('Volver', 'Back')}
            title={chatText('Volver', 'Back')}
          >
            ←
          </button>
          <div style={styles.titleWrap}>
            <div style={styles.headerUser}>
              <div
                role="img"
                aria-label={peerName || 'Contacto'}
                style={{
                  ...styles.headerAvatar,
                  // Paint instantly with inline data URI; swap to real photo once preloaded via resolvedPeerPhoto
                  backgroundImage: `url(${resolvedPeerPhoto || DEFAULT_AVATAR_DATA})`,
                }}
              />
              <div style={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}>
                {(() => {
                  const lastSeen = peerLastSeen ? Number(peerLastSeen) : 0;
                  const hasLastSeen = !!lastSeen;

                  // Typing has priority
                  const isTyping = typingUsers && typingUsers.size > 0;

                  // Dot tones (honestos): typing (violeta), activo ahora (verde), visto (dorado), desconocido (gris)
                  const tone = isTyping
                    ? 'typing'
                    : (peerOnline ? 'online' : (hasLastSeen ? 'seen' : 'unknown'));

                  const dotStyle = (() => {
                    if (tone === 'typing') {
                      return {
                        background: 'rgba(155,48,255,0.95)',
                        boxShadow: '0 0 14px rgba(155,48,255,0.45), 0 0 6px rgba(255,255,255,0.10)',
                        borderColor: 'rgba(255,255,255,0.65)',
                      };
                    }
                    if (tone === 'online') {
                      return {
                        background: 'rgba(34,197,94,0.95)',
                        boxShadow: '0 0 14px rgba(34,197,94,0.45), 0 0 6px rgba(255,255,255,0.10)',
                        borderColor: 'rgba(255,255,255,0.65)',
                      };
                    }
                    if (tone === 'seen') {
                      return {
                        background: 'rgba(218,165,32,0.98)',
                        boxShadow: '0 0 14px rgba(218,165,32,0.45), 0 0 6px rgba(255,255,255,0.10)',
                        borderColor: 'rgba(255,255,255,0.65)',
                      };
                    }
                    return {
                      background: 'rgba(255,255,255,0.45)',
                      boxShadow: '0 0 10px rgba(255,255,255,0.12)',
                      borderColor: 'rgba(255,255,255,0.55)',
                    };
                  })();

                  return (
                    <div style={styles.nameRow}>
                      <h1 style={styles.title} title={peerName || 'Contacto'}>
                        {peerName || 'Contacto'}
                      </h1>

                      {peerSign ? (
                        <span
                          aria-hidden="true"
                          style={{ fontSize: isMobile ? 16 : 15, opacity: 0.95, flexShrink: 0 }}
                          title={String(peerSign)}
                        >
                          {signToEmoji(peerSign)}
                        </span>
                      ) : null}

                      <span
                        aria-hidden="true"
                        style={{ ...styles.statusDotWrap, marginLeft: 2 }}
                        title={
                          tone === 'typing'
                            ? 'Escribiendo…'
                            : (tone === 'online'
                              ? 'Activo ahora'
                              : (tone === 'seen'
                                ? 'Actividad registrada'
                                : 'Estado desconocido'))
                        }
                      >
                        <span style={{ ...styles.statusDot, ...dotStyle }} />
                      </span>
                    </div>
                  );
                })()}

                {(() => {
                  // ✅ Premium + honesto: evitamos “En línea / Desconectado”. Mostramos “actividad”.
                  // 1) Si está escribiendo, mostramos “Escribiendo…”
                  if (typingUsers && typingUsers.size > 0) {
                    return (
                      <div style={styles.statusRow}>
                        <span style={styles.statusPill}>
                          <span aria-hidden="true">✍️</span>
                          <span style={styles.statusText}>Escribiendo…</span>
                        </span>
                      </div>
                    );
                  }

                  const lastSeen = peerLastSeen ? Number(peerLastSeen) : 0;
                  const hasLastSeen = !!lastSeen;

                  // 2) Si el backend/presencia indica que está activo ahora
                  if (peerOnline) {
                    return (
                      <div style={styles.statusRow}>
                        <span style={styles.statusPill}>
                          <span aria-hidden="true">🟢</span>
                          <span style={styles.statusText}>Activo ahora</span>
                        </span>
                      </div>
                    );
                  }

                  // 3) Si tenemos lastSeen real: “Activo hace …”
                  if (hasLastSeen) {
                    const relRaw = formatRelativeTime(lastSeen);
                    const relNorm = String(relRaw || '').trim().toLowerCase();

                    // En mobile, si el helper devuelve “ahora/recién”, eso confunde cuando está offline.
                    // Forzamos a un texto tipo “hace 1 min” para que sea inequívoco.
                    const relOut = (isMobile && (
                      relNorm === 'ahora' ||
                      relNorm === 'justo ahora' ||
                      relNorm === 'recién' ||
                      relNorm === 'recien'
                    ))
                      ? 'hace 1 min'
                      : relRaw;

                    return (
                      <div style={styles.statusRow}>
                        <span style={styles.statusPill}>
                          <span aria-hidden="true">🕒</span>
                          <span style={styles.statusText}>{`Activo ${relOut}`}</span>
                        </span>
                      </div>
                    );
                  }

                  // 4) Sin lastSeen real: no mostramos nada (evita confusión)
                  return null;
                })()}
              </div>
            </div>
          </div>
        </div>

        {/* Mensajes */}
        {loading ? (
          <div
            style={{
              ...styles.listLoading,
              paddingBottom: `calc(18px + ${safeBottom} + ${(kb.open ? composerH : 0)}px)`
            }}
          >
            <div style={styles.skeletonRow(false)}>
              <div style={styles.skeletonBubble(false)} />
            </div>
            <div style={styles.skeletonRow(true)}>
              <div style={styles.skeletonBubble(true)} />
            </div>
            <div style={styles.skeletonRow(false)}>
              <div style={styles.skeletonBubble(false)} />
            </div>
            <div style={styles.skeletonRow(true)}>
              <div style={styles.skeletonBubble(true)} />
            </div>
          </div>
        ) : (
          <div
            style={{
              ...styles.list,
              paddingBottom: `calc(18px + ${safeBottom} + ${(kb.open ? composerH : 0)}px)`
            }}
          >
            {messages.length === 0 ? (
              <div
                style={{
                  color: 'rgba(248,241,255,0.70)',
                  fontStyle: 'italic',
                  textAlign: 'center',
                  padding: '28px 14px',
                  borderRadius: 22,
                  border: '1px solid rgba(255,255,255,0.10)',
                  background: 'linear-gradient(145deg, rgba(255,255,255,0.06), rgba(138,43,226,0.10))',
                }}
              >
                {chatText('No hay mensajes aún…', 'No messages yet…')}
              </div>
            ) : (
              messages.map((m) => {
                const isMine =
                  myUserId &&
                  m?.sender &&
                  String(m.sender._id || m.sender) === String(myUserId);
                return (
                  <div key={String(m._id || m.id || `${(m.conversation && typeof m.conversation === 'object' && (m.conversation._id || m.conversation.id)) || m.conversation || conversationId}-${m.createdAt || m.timestamp || ''}-${(typeof m.sender==='object' ? m.sender?._id : m.sender) || 'unk'}-${(m.text ?? m.content ?? m.body ?? '').slice(0,32)}`)} style={styles.bubbleRow(isMine)}>
                    <div style={styles.bubble(isMine)}>
                      {!isMine && (
                        <div style={styles.name}>
                          {(m.sender && typeof m.sender === 'object' && m.sender.fullName)
                            ? m.sender.fullName
                            : (peerNameRef.current || 'Contacto')}
                          <span style={styles.signEmoji}>
                            {signToEmoji(
                              (m.sender && typeof m.sender === 'object' && (m.sender.zodiacSign || peerSign)) ||
                              peerSign
                            )}
                          </span>
                        </div>
                      )}
                      <div style={styles.text}>{m.text ?? m.content ?? m.body ?? ''}</div>
                      <div
                        style={{
                          ...styles.time,
                          ...(isMine ? { color: '#F3C96B' } : { color: 'rgba(213,190,255,0.72)' }),
                        }}
                      >
                        {formatDateTime(m.createdAt || m.timestamp)}
                      </div>
                    </div>
                  </div>
                );
              })
            )}
            <div ref={bottomRef} />
          </div>
        )}

        {/* Typing */}
        {typingUsers.size > 0 && <div style={styles.typing}>Escribiendo…</div>}

        {/* Picker de emoji */}
        {showEmoji && (
  <div ref={emojiPopupRef} style={styles.emojiPopup}>
    <EmojiPicker
      onEmojiClick={onEmojiClick}
      searchDisabled={true}
      skinTonesDisabled={false}
      lazyLoadEmojis={true}
    />
  </div>
)}
        

        {/* Composer */}
        <form
          onSubmit={onSubmit}
          onFocus={() => { setActiveConv(conversationId); bumpLastRead(conversationId, Date.now()); }}
          onBlur={() => { bumpLastRead(conversationId, Date.now()); clearActiveConv(); }}
          style={{
            ...styles.composerWrap,
            ...(isMobile && kb.open
              ? {
                  position: 'fixed',
                  left: 0,
                  right: 0,
                  bottom: 0,
                  width: '100%',
                  zIndex: 100,
                }
              : {}),
          }}
          autoComplete="off"
        >
          <label htmlFor="chatMessage" style={styles.srOnly}>{chatText('Mensaje', 'Message')}</label>

          <button
  ref={emojiBtnRef}
  type="button"
  aria-label={chatText('Insertar emoji', 'Insert emoji')}
  title={chatText('Insertar emoji', 'Insert emoji')}
  style={styles.emojiBtn}
  onClick={(e) => { e.preventDefault(); e.stopPropagation(); setShowEmoji((s) => !s); }}
  onMouseDown={(e) => { e.preventDefault(); e.stopPropagation(); }}
>
  😀
</button>

          <input
            ref={inputRef}
            type="text"
            id="chatMessage"
            name="chatMessage"
            aria-label={chatText('Mensaje', 'Message')}
            autoComplete="off"
            value={draft}
            onChange={onChange}
            disabled={isSending}
            style={styles.input}
            placeholder={chatText('Escribí un mensaje…', 'Write a message…')}
          />

          <button
            type="submit"
            disabled={isSending || !draft.trim()}
            style={styles.sendBtn}
            onMouseDown={(e) => (e.currentTarget.style.transform = 'scale(0.98)')}
            onMouseUp={(e) => (e.currentTarget.style.transform = 'scale(1)')}
          >
            {chatText('Enviar', 'Send')}
          </button>
        </form>
      </div>
    </div>
  );
}
